import { Area, AreaChart, ResponsiveContainer, Tooltip, ReferenceLine } from 'recharts';
import { Target, AlertTriangle, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export interface SparklineDataPoint {
  value: number;
}

export interface ThresholdConfig {
  value: number;
  type: 'target' | 'warning' | 'danger';
  label?: string;
}

interface TrendSparklineProps {
  data: SparklineDataPoint[];
  isPositive?: boolean;
  className?: string;
  height?: number;
  threshold?: ThresholdConfig;
}

// R-code style colors for better visibility on white backgrounds
const thresholdColors = {
  target: '#1F4E79',
  warning: '#B9770E',
  danger: '#C0392B'
};

const thresholdIcons = {
  target: Target,
  warning: AlertTriangle,
  danger: AlertCircle
};

const thresholdLabels = {
  target: 'Goal target',
  warning: 'Warning threshold',
  danger: 'Critical threshold'
};

const CustomTooltip = ({ active, payload, isPositive, threshold }: any) => {
  if (active && payload && payload.length) {
    const value = payload[0].value;
    const isAboveThreshold = threshold && value >= threshold.value;
    
    return (
      <div className={cn(
        'px-2 py-1 rounded text-xs font-medium shadow-lg border bg-white',
        isPositive 
          ? 'text-[#1E8449] border-[#1E8449]/30' 
          : 'text-[#C0392B] border-[#C0392B]/30'
      )}>
        <span>{value}</span>
        {threshold && (
          <span className="text-gray-500 ml-1">
            ({isAboveThreshold ? '↑' : '↓'} {threshold.label || 'target'})
          </span>
        )}
      </div>
    );
  }
  return null;
};

export default function TrendSparkline({ 
  data, 
  isPositive = true, 
  className,
  height = 32,
  threshold
}: TrendSparklineProps) {
  if (!data || data.length < 2) return null;

  // Use R-code style colors for better visibility on white backgrounds
  const color = isPositive ? '#1E8449' : '#C0392B';
  const thresholdColor = threshold ? thresholdColors[threshold.type] : undefined;
  const ThresholdIcon = threshold ? thresholdIcons[threshold.type] : null;
  
  // Calculate current value vs threshold
  const currentValue = data[data.length - 1]?.value ?? 0;
  const isAboveThreshold = threshold ? currentValue >= threshold.value : null;
  const progressPercent = threshold ? Math.min(100, Math.round((currentValue / threshold.value) * 100)) : null;

  return (
    <div className={cn('w-full relative', className)} style={{ height }}>
      {threshold && ThresholdIcon && (
        <TooltipProvider>
          <UITooltip>
            <TooltipTrigger asChild>
              <div className={cn(
                'absolute -top-1 -right-1 z-10 flex items-center justify-center',
                'w-4 h-4 rounded-full border bg-white',
                isAboveThreshold 
                  ? 'text-[#1E8449] border-[#1E8449]/40' 
                  : 'text-[#B9770E] border-[#B9770E]/40'
              )}>
                <ThresholdIcon className="w-2.5 h-2.5" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs bg-white border border-gray-200 shadow-lg">
              <p className="font-medium text-gray-900">{thresholdLabels[threshold.type]}</p>
              <p className="text-gray-500">
                {threshold.label || 'Target'}: {threshold.value}
              </p>
              <div className="mt-1.5 pt-1.5 border-t border-gray-200">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-gray-500">Current:</span>
                  <span className={cn(
                    'font-medium',
                    isAboveThreshold ? 'text-[#1E8449]' : 'text-[#B9770E]'
                  )}>
                    {currentValue} ({progressPercent}%)
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className={cn(
                      'h-full rounded-full transition-all',
                      isAboveThreshold ? 'bg-[#1E8449]' : 'bg-[#B9770E]'
                    )}
                    style={{ width: `${Math.min(100, progressPercent || 0)}%` }}
                  />
                </div>
              </div>
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <defs>
            <linearGradient id={`sparklineGradient-${isPositive ? 'pos' : 'neg'}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.3} />
              <stop offset="100%" stopColor={color} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          {threshold && (
            <ReferenceLine 
              y={threshold.value} 
              stroke={thresholdColor}
              strokeDasharray="4 2"
              strokeWidth={1}
              strokeOpacity={0.7}
            />
          )}
          <Tooltip 
            content={<CustomTooltip isPositive={isPositive} threshold={threshold} />}
            cursor={{ stroke: color, strokeWidth: 1, strokeDasharray: '3 3' }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={1.5}
            fill={`url(#sparklineGradient-${isPositive ? 'pos' : 'neg'})`}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
